<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { CheckCircle2, CircleHelp, XCircle, Clock, ExternalLink, Wallet } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { API, HOST } from '@/utils/http'
import type { BridgeOrganization, BridgeCustomer, BridgeTransfer } from '@/types/bridge'
import type OrgMember from '@/types/OrgMember'
import OrgKYBOnboarding from './OrgKYBOnboarding.vue'
import OrgPaymentsMembersList from './OrgPaymentsMembersList.vue'
import BridgeTransfersList from '@/pages/Admin/Payments/BridgeTransfersList.vue'

const store = useStore()
const route = useRoute()
const member = computed(() => store.getters['getMember'])
const isSuperUser = computed(() => member.value?.is_super_user)
const orgId = computed(() => parseInt(route.params.org_id as string))

const bridgeOrg = ref<BridgeOrganization | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const view = ref<'status' | 'kyb'>('status')
const activeTab = ref<'customers' | 'transfers'>('customers')
const showApprovedDetails = ref(false)

const allCustomers = ref<BridgeCustomer[]>([])
const allTransfers = ref<BridgeTransfer[]>([])
const transfersLoading = ref(false)

const orgMembers = computed<OrgMember[]>(() => store.getters['org/getCurrentMembers'] ?? [])

const orgTransfers = computed<BridgeTransfer[]>(() => {
  const memberIds = new Set(orgMembers.value.map(m => m.member_id))
  const customerIds = new Set(
    allCustomers.value.filter(c => memberIds.has(c.member_id)).map(c => c.id)
  )
  if (bridgeOrg.value) customerIds.add(bridgeOrg.value.id)
  return allTransfers.value.filter(
    t => customerIds.has(t.sender_customer_id) || customerIds.has(t.receiver_customer_id)
  )
})

type KybOverallStatus = 'approved' | 'rejected' | 'awaiting_ubo' | 'in_progress' | 'not_registered'

const kybStatus = computed<KybOverallStatus>(() => {
  if (!bridgeOrg.value) return 'not_registered'
  const kyb = bridgeOrg.value.kyc_status
  const tos = bridgeOrg.value.tos_status
  if (tos === 'approved' && kyb === 'approved') return 'approved'
  if (['rejected', 'paused', 'offboarded'].includes(kyb)) return 'rejected'
  if (kyb === 'awaiting_ubo') return 'awaiting_ubo'
  return 'in_progress'
})

const kycUrl = computed(() => bridgeOrg.value?.link?.kyc_url ?? null)

const fetchBridgeOrg = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await API({ method: 'GET', url: `${HOST}/payments/bridge/organizations` })
    const orgs: BridgeOrganization[] = response.data
    bridgeOrg.value = orgs.find(o => o.organization_id === orgId.value) ?? null
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Failed to fetch payment status'
  } finally {
    loading.value = false
  }
}

const fetchTransfersData = async () => {
  transfersLoading.value = true
  try {
    const [customersRes, transfersRes] = await Promise.all([
      API({ method: 'GET', url: `${HOST}/payments/bridge/customers` }),
      API({ method: 'GET', url: `${HOST}/payments/bridge/transfers`, params: { limit: 100 } })
    ])
    allCustomers.value = customersRes.data
    allTransfers.value = transfersRes.data
  } finally {
    transfersLoading.value = false
  }
}

onMounted(() => {
  if (isSuperUser.value) {
    fetchBridgeOrg()
    store.dispatch('org/fetchOrgMembers', orgId.value)
  }
})

watch(activeTab, (tab) => {
  if (tab === 'transfers' && isSuperUser.value && allTransfers.value.length === 0 && !transfersLoading.value) {
    fetchTransfersData()
  }
})
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- KYB onboarding sub-view -->
    <OrgKYBOnboarding
      v-if="view === 'kyb'"
      :bridge-org="bridgeOrg"
      :org-id="orgId"
      @back="view = 'status'"
    />

    <template v-else>
    <!-- Non-super-user empty state -->
    <div v-if="!isSuperUser" class="flex-1 flex flex-col items-center justify-center gap-3 text-muted-foreground p-8 text-center">
      <Wallet class="h-10 w-10 opacity-30" />
      <p class="text-sm">Payments are not available for your account.</p>
    </div>

    <!-- Super user content -->
    <Tabs v-else v-model="activeTab" class="flex-1 flex flex-col overflow-hidden">

      <!-- Header with tabs + KYB badge -->
      <div class="px-4 py-2 border-b flex-shrink-0 flex items-center justify-between">
        <TabsList>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="transfers">Transfers</TabsTrigger>
        </TabsList>
        <button
          v-if="kybStatus === 'approved'"
          @click="showApprovedDetails = !showApprovedDetails"
          class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200 hover:bg-green-200 transition-colors"
        >
          <CheckCircle2 class="h-3 w-3" />
          KYB Complete
        </button>
      </div>

      <!-- Customers tab -->
      <TabsContent value="customers" class="flex-1 overflow-y-auto p-4 space-y-6 mt-0">

      <!-- Loading -->
      <div v-if="loading" class="space-y-3">
        <div class="h-4 bg-muted rounded animate-pulse w-1/2" />
        <div class="h-20 bg-muted rounded animate-pulse" />
      </div>

      <!-- Error -->
      <div v-else-if="error" class="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
        {{ error }}
      </div>

      <template v-else>
        <!-- KYB Status Card — hidden when approved and details collapsed -->
        <div v-if="kybStatus !== 'approved' || showApprovedDetails" class="rounded-lg border p-4 space-y-3 max-w-[600px]">
          <div class="flex items-center justify-between">
            <span class="text-xs font-medium text-muted-foreground uppercase tracking-wide">KYB Status</span>
            <Badge
              v-if="kybStatus === 'rejected'"
              class="bg-red-100 text-red-700 border-red-200 hover:bg-red-100"
            >
              <XCircle class="h-3 w-3 mr-1" />
              Rejected
            </Badge>
            <Badge
              v-else-if="kybStatus === 'awaiting_ubo'"
              class="bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100"
            >
              <Clock class="h-3 w-3 mr-1" />
              Awaiting UBO
            </Badge>
            <Badge
              v-else-if="kybStatus === 'in_progress'"
              class="bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100"
            >
              <CircleHelp class="h-3 w-3 mr-1" />
              {{ bridgeOrg?.kyc_status ?? 'In Progress' }}
            </Badge>
            <Badge
              v-else-if="kybStatus === 'not_registered'"
              class="bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-100"
            >
              Not Registered
            </Badge>
          </div>

          <!-- Approved: expanded details -->
          <div v-if="kybStatus === 'approved'" class="space-y-2">
            <div class="flex items-center gap-2 text-green-700">
              <CheckCircle2 class="h-5 w-5 flex-shrink-0" />
              <span class="text-sm font-medium">Payments enabled</span>
            </div>
            <p class="text-xs text-muted-foreground">
              This organization has completed KYB verification and is cleared for payments.
            </p>
            <div class="pt-1 space-y-1 text-xs">
              <div class="flex items-center justify-between">
                <span class="text-muted-foreground">ToS</span>
                <Badge class="bg-green-100 text-green-700 border-green-200 text-xs py-0">approved</Badge>
              </div>
              <div class="flex items-center justify-between">
                <span class="text-muted-foreground">KYB</span>
                <Badge class="bg-green-100 text-green-700 border-green-200 text-xs py-0">approved</Badge>
              </div>
            </div>
          </div>

          <!-- Not registered: no Bridge record -->
          <div v-else-if="kybStatus === 'not_registered'" class="space-y-3">
            <p class="text-xs text-muted-foreground">
              This organization has not been registered for payments yet. Start KYB onboarding to enable payment capabilities.
            </p>
            <p class="text-xs text-muted-foreground">
              A one-time $10 onboarding fee applies, billed through payments once onboarding is complete.
            </p>
            <Button class="w-full max-w-[250px] h-10" @click="view = 'kyb'">
              <span class="flex items-center justify-center whitespace-nowrap">
                Start KYB
                <ExternalLink class="h-3.5 w-3.5 ml-1.5 flex-shrink-0" />
              </span>
            </Button>
          </div>

          <!-- In progress / awaiting UBO / rejected -->
          <div v-else class="space-y-3">
            <p class="text-xs text-muted-foreground">
              <template v-if="kybStatus === 'rejected'">
                KYB verification was not approved. Contact Bridge support to resolve.
              </template>
              <template v-else-if="kybStatus === 'awaiting_ubo'">
                Waiting for Ultimate Beneficial Owner information to be submitted.
              </template>
              <template v-else>
                KYB onboarding is in progress. Continue the verification flow to complete setup.
              </template>
            </p>
            <Button
              v-if="kycUrl && kybStatus !== 'rejected'"
              size="sm"
              class="w-full"
              as-child
            >
              <a :href="kycUrl" target="_blank" rel="noopener noreferrer">
                Continue KYB
                <ExternalLink class="h-3 w-3 ml-1.5" />
              </a>
            </Button>
            <Button
              v-else-if="kybStatus !== 'rejected'"
              size="sm"
              variant="outline"
              class="w-full"
              as-child
            >
              <a href="/admin/payments">
                View in Admin
                <ExternalLink class="h-3 w-3 ml-1.5" />
              </a>
            </Button>
          </div>
        </div>
        <!-- Members list — only once org KYB is complete -->
        <OrgPaymentsMembersList v-if="kybStatus === 'approved'" :org-id="orgId" />
      </template>
      </TabsContent>

      <!-- Transfers tab -->
      <TabsContent value="transfers" class="flex-1 overflow-y-auto p-4 mt-0">
        <div v-if="!transfersLoading && orgTransfers.length === 0 && allTransfers.length === 0" class="text-xs text-muted-foreground mb-2">
          No transfers found for this organization.
        </div>
        <div v-else-if="!transfersLoading && orgTransfers.length === 0" class="text-xs text-muted-foreground mb-2">
          No transfers found for this organization's members.
        </div>
        <BridgeTransfersList :transfers="orgTransfers" :loading="transfersLoading" />
      </TabsContent>

    </Tabs>
    </template>
  </div>
</template>
